import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Form, Input, Popconfirm } from 'antd';
import InputNumber from 'antd/lib/input-number';
import { CardMainLayout, Catcher, cardLayoutOptions } from '../../index';
import { INonRecurringCharges, INonRecurringChargesItem, NotificationType } from '../../../interfaces';
import { useCompanyInfo, useNonRecurringChargesList } from '../../../hooks';
import {
    addNotification,
    apiAddFormErrorMessages,
    apiMessagesGetFormRules,
    deleteNonRecurringChargesList,
    postNonRecurringChargesList,
    putNonRecurringChargesList,
} from '../../../middleware';
import { globalHexId } from '../../../instruments';
import { tB, tNM } from '../../../translate';
import Styles from '../../styles/m_itemCardStyles.less';

interface IFormItem extends INonRecurringChargesItem {
    remove?: (id: string | number, listId: string | number) => void;
    companyId: string;
}

function FormItem({ remove, companyId, ...props }: IFormItem) {
    const [form] = Form.useForm();
    const [canSave, setCanSave] = useState(false);
    const [initialData, setInitialData] = useState<INonRecurringChargesItem>();

    const isNew = typeof initialData?.id === 'string';

    const validationInputs = [...apiMessagesGetFormRules];

    const onFormChange = () => {
        const { id, ...props } = form.getFieldsValue();
        const { id: newId, ...initProps } = initialData || {};
        setCanSave(JSON.stringify(props) !== JSON.stringify(initProps));
    };

    const onSave = async () => {
        try {
            await form.validateFields();

            const values = form.getFieldsValue();

            let response;

            if (!isNew) {
                response = await putNonRecurringChargesList(`${initialData?.id}`, companyId, values);
            } else {
                response = await postNonRecurringChargesList(companyId, values);
            }

            const { isError, resp } = response;

            if (isError) {
                return apiAddFormErrorMessages(resp || {}, form);
            }

            addNotification({
                type: NotificationType.SUCCESS,
                message: 'Non Recurring cost has been saved successfully',
            });

            const monthlyCharges = resp as unknown as INonRecurringChargesItem;

            console.log(monthlyCharges);

            setInitialData({ id: monthlyCharges.id, ...values });
            setCanSave(false);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        setInitialData(props);
    }, []);

    useEffect(() => {
        form.setFieldsValue(props);
    }, [JSON.stringify(props)]);

    return (
        <Form
            form={form}
            name={`${props.name}${props.id}`}
            initialValues={props}
            layout={'vertical'}
            className={'formStyle'}
            onValuesChange={onFormChange}
        >
            <div className={Styles.mrcFormItemContainer}>
                <div>
                    <Form.Item name="name" label={'Name'} rules={apiMessagesGetFormRules}>
                        <Input className={Styles.mrcFormItemInput} />
                    </Form.Item>
                </div>
                <div className={Styles.mrcFormItemAmountContainer}>
                    <Form.Item name="amountCents" label={'Amount'} rules={validationInputs} style={{ width: '100%' }}>
                        <InputNumber
                            step={1}
                            min={0.01}
                            precision={2}
                            className={Styles.mrcFormItemInput}
                            addonBefore={'EUR'}
                        />
                    </Form.Item>
                    <div className={Styles.mrcFormItemBottomBar}>
                        <Button type={'primary'} disabled={!canSave} onClick={onSave}>
                            {tB('Save')}
                        </Button>
                        {remove ? (
                            <Popconfirm
                                cancelText={tB('Cancel')}
                                disabled={isNew}
                                okText={tB('Delete')}
                                onConfirm={() => remove(initialData?.id || '', props.id)}
                                placement="top"
                                title={tNM('Confirm deletion.')}
                            >
                                {isNew ? (
                                    <Button danger onClick={() => remove(initialData?.id || '', props.id)}>
                                        Delete
                                    </Button>
                                ) : (
                                    <Button danger>Delete</Button>
                                )}
                            </Popconfirm>
                        ) : null}
                    </div>
                </div>
            </div>
        </Form>
    );
}

export const EditCompanyFieldsNrc: React.FC = observer(() => {
    const { id: companyId } = useCompanyInfo();
    const monthlyRecurringChargesList = useNonRecurringChargesList();
    const [newFormArray, setNewFormArray] = useState<INonRecurringCharges>([]);

    const mrcFormItem = useRef<HTMLDivElement>(null);

    const scrollBottom = () => {
        if (mrcFormItem.current) {
            const { scrollHeight } = mrcFormItem.current;
            mrcFormItem.current.scrollTop = scrollHeight;
        }
    };

    const addNewItem = () => {
        const id = globalHexId.getId();

        const initNewItemValue: INonRecurringChargesItem = {
            amountCents: 0,
            id,
            name: '',
        };

        setNewFormArray([...newFormArray, initNewItemValue]);
    };

    const removeItem = async (id: string | number, listId: string | number) => {
        console.log(id, listId);

        const isNew = typeof id === 'string';

        if (!isNew) {
            const { isError } = await deleteNonRecurringChargesList(`${id}`, companyId);

            if (isError) {
                return addNotification({
                    type: NotificationType.ERROR,
                    message: 'An error has occurred while deleting',
                    isUsersError: true,
                });
            }
        }

        const filteredArr = newFormArray.filter((value) => value.id !== listId);

        setNewFormArray([...filteredArr]);
        if (!isNew) {
            addNotification({
                type: NotificationType.SUCCESS,
                message: 'Non Recurring cost has been deleted successfully',
                isUsersError: true,
            });
        }
    };

    useEffect(() => {
        setNewFormArray([...monthlyRecurringChargesList]);
    }, [JSON.stringify(monthlyRecurringChargesList)]);

    useEffect(() => {
        scrollBottom();
    }, [newFormArray]);

    return (
        <Catcher>
            <CardMainLayout
                name={'Non Recurring Costs'}
                columnOption={cardLayoutOptions.mrcEditForm}
                idSpinners={[
                    'GET_COMPANY_NON_RECURRING_PAYMENTS',
                    'POST_COMPANY_NON_RECURRING_PAYMENTS',
                    'PUT_COMPANY_NON_RECURRING_PAYMENTS',
                    'DELETE_COMPANY_NON_RECURRING_PAYMENTS',
                ]}
            >
                <div className={Styles.mrcFormItem} ref={mrcFormItem}>
                    {newFormArray.map((field: INonRecurringChargesItem) => {
                        return (
                            <div key={field.id}>
                                <FormItem {...field} remove={removeItem} companyId={companyId} />
                            </div>
                        );
                    })}
                </div>
                <Button type="dashed" onClick={addNewItem} className={Styles.mrcAddButton}>
                    Add New
                </Button>
            </CardMainLayout>
        </Catcher>
    );
});
